import { request } from "@data/datoRequest"
import { fetchPdmProductsRecords } from "@views/Upholstery/pdm/pdmStepsClient"
import type { Step, UpholsteryCar } from "@views/Upholstery/types"
import { upholsteryStepsQuery } from "@views/Upholstery/graphql"
import { applyDerivedBodySuffixesToSteps } from "../steps/applyDerivedBodySuffixesToSteps"
import { applyDerivedM3SuffixesFromPdmBases } from "../steps/applyDerivedM3SuffixesFromPdmBases"
import { buildPdmM3Lookup } from "../steps/resolveM3SkuFromPdmBases"
import { buildSyntheticUpholsteryCar } from "./buildSyntheticUpholsteryCar"
import {
  deriveM3SkusFromPdmBases,
  type DerivedM3Skus,
} from "../steps/deriveM3SkusFromPdmBases"
import {
  logStyleStepsSource,
  logVehicleRecordSource,
  type VehicleRecordSource,
} from "../debug/debugStyleStepsSource"
import {
  fetchUpholsteryStepsV2,
  overlayMissingBuildPricesFromCar,
  overlayMissingFactoryPricesFromKit,
  spliceGlobalStyleSteps,
  withoutGlobalStyleSteps,
} from "../steps/fetchUpholsteryStepsV2"
import { isPdmOnlyVehiclesEnabled } from "../flags/isPdmOnlyVehiclesEnabled"
import { isPdmStructureV2Enabled } from "../flags/isPdmStructureV2Enabled"
import { isUpholsteryStepsV2Enabled } from "../flags/isUpholsteryStepsV2Enabled"
import { flattenMatchingApplications } from "../../pdm/normalizePdm"
import { overlayDatoDesignDefaultsOnSteps } from "../steps/overlayDatoDesignDefaultsOnSteps"
import { resolveStepsWithPdm } from "../steps/resolveStepsWithPdm"
import { VehicleNotFoundError } from "./vehicleSelectionErrors"
import { fetchShopifyVariantsByMake } from "../shopify/fetchShopifyVariantsByMake"

function productField(
  product: Record<string, unknown>,
  snake: string,
  camel: string
): string {
  const raw = product[snake] ?? product[camel] ?? ""
  return String(raw).trim()
}

export interface UpholsteryVehicleSelectionResult {
  datoVehicle: UpholsteryCar
  stepsWithPdm: Step[]
  vehicleData: UpholsteryCar
  /** `dato` when a CMS record exists; `pdm-only` when a synthetic shell was used. */
  vehicleSource: VehicleRecordSource
}

/**
 * Load one vehicle for the configurator.
 *
 * When Steps V2 is enabled (`NEXT_PUBLIC_UPHOLSTERY_STEPS_V2` and PDM V2):
 * - Style steps come from global Steps V2 only (cached after first fetch).
 * - Per-car nested Build/Kit/Pattern/Design/Material are discarded.
 * - If Steps V2 fails or is incomplete, this throws (no silent fallback).
 *
 * When Dato has no `upholsteryCar`:
 * - If `NEXT_PUBLIC_UPHOLSTERY_PDM_ONLY_VEHICLES` + PDM V2 → synthetic shell
 * - Otherwise → VehicleNotFoundError (same warning as today)
 *
 * When Steps V2 is off: use the car's nested steps as before (or synthetic PDM shells).
 */
export async function loadUpholsteryVehicleSelection(
  make: string,
  model: string,
  year: string
): Promise<UpholsteryVehicleSelectionResult> {
  const useGlobalStyleSteps = isUpholsteryStepsV2Enabled()

  const vehicleRequest = request({
    query: upholsteryStepsQuery,
    variables: {
      make,
      model,
      years: year,
    },
  })

  // Brand Shopify map — fail-soft, parallel with vehicle (+ style steps when V2).
  const shopifyVariantsRequest = fetchShopifyVariantsByMake(make).catch(
    () => [] as Awaited<ReturnType<typeof fetchShopifyVariantsByMake>>
  )

  const [response, globalStyleSteps] = useGlobalStyleSteps
    ? await Promise.all([
        vehicleRequest,
        fetchUpholsteryStepsV2(),
        shopifyVariantsRequest,
      ]).then(([vehicleResponse, steps]) => [vehicleResponse, steps] as const)
    : await Promise.all([vehicleRequest, shopifyVariantsRequest]).then(
        ([vehicleResponse]) => [vehicleResponse, null] as const
      )

  let vehicle = response.upholsteryCar as UpholsteryCar | null
  let vehicleSource: VehicleRecordSource = "dato"

  if (!vehicle) {
    if (isPdmOnlyVehiclesEnabled()) {
      vehicle = buildSyntheticUpholsteryCar(make, model, year)
      vehicleSource = "pdm-only"
    } else {
      throw new VehicleNotFoundError(make, model, year)
    }
  }

  logVehicleRecordSource({
    source: vehicleSource,
    make,
    model,
    year,
  })

  let stepsForMerge: Step[]
  if (useGlobalStyleSteps) {
    // Flag on: V2 is required. fetchUpholsteryStepsV2 already threw if incomplete.
    // Strip leftover per-car style steps so they cannot act as a backup source.
    if (!globalStyleSteps?.length) {
      throw new Error(
        "[Upholstery Steps V2] Global style steps were not loaded."
      )
    }
    const carStepsBeforeStrip = vehicle.steps ?? []
    const carShellSteps = withoutGlobalStyleSteps(carStepsBeforeStrip)
    stepsForMerge = overlayMissingFactoryPricesFromKit(
      overlayMissingBuildPricesFromCar(
        spliceGlobalStyleSteps(carShellSteps, globalStyleSteps),
        carStepsBeforeStrip
      )
    )
  } else {
    stepsForMerge = vehicle.steps ?? []
  }

  logStyleStepsSource({
    flagOn: useGlobalStyleSteps,
    steps: stepsForMerge,
  })

  // PDM base_part_number drives custom M3 segments.
  // - pdm-only: no Dato vehicle.sku → derive vehicle LCP / full base
  // - all PDM V2 cars: map base remainders onto the option that distinguishes
  //   bases (e.g. Traverse middle row E/F), not only body-step
  let derivedM3: DerivedM3Skus | null = null
  let flatForM3: ReturnType<typeof flattenMatchingApplications> = []
  if (isPdmStructureV2Enabled()) {
    const products = (await fetchPdmProductsRecords()) ?? []
    const yearNum = Number.parseInt(year, 10)
    flatForM3 =
      Number.isFinite(yearNum) && products.length > 0
        ? flattenMatchingApplications(products, make, model, yearNum)
        : []
    const bases = flatForM3
      .map((row) =>
        productField(row.product, "base_part_number", "basePartNumber")
      )
      .filter(Boolean)

    if (vehicleSource === "pdm-only") {
      derivedM3 = deriveM3SkusFromPdmBases(bases)
      vehicle = { ...vehicle, sku: derivedM3.vehicleSku }
    }
  }

  let stepsWithPdm = await resolveStepsWithPdm(
    { ...vehicle, steps: stepsForMerge },
    make,
    model,
    year
  )

  if (
    vehicleSource === "pdm-only" &&
    derivedM3 &&
    derivedM3.bodySuffixes.length > 0
  ) {
    stepsWithPdm = applyDerivedBodySuffixesToSteps(
      stepsWithPdm,
      derivedM3.bodySuffixes
    )
  }

  // Dynamic: assign CHE2408E/F-style remainders onto middle (or other) options
  // when PDM bases differ by that dimension. Safe for other cars — only fills
  // empty option SKUs when a clear 1:1 base↔option mapping exists.
  if (flatForM3.length > 0 && vehicle.sku) {
    stepsWithPdm = applyDerivedM3SuffixesFromPdmBases(
      stepsWithPdm,
      flatForM3,
      vehicle.sku
    )
  }

  // Preserve per-car OEM perforation defaults from the original Dato car.
  // Steps V2's shared Design step replaces those defaults with CMS `value`
  // (often the key "perforation"), which would otherwise select p0/None.
  if (vehicleSource === "dato") {
    stepsWithPdm = overlayDatoDesignDefaultsOnSteps(
      stepsWithPdm,
      vehicle.steps ?? []
    )
  }

  const vehicleData: UpholsteryCar = {
    id: vehicle.id,
    make,
    model,
    years: year,
    sku: vehicle.sku,
    image: vehicle.image,
    steps: stepsWithPdm,
    trims: vehicle.trims || "",
    driverFactorySeatType: vehicle.driverFactorySeatType || "",
    passengerFactorySeatType: vehicle.passengerFactorySeatType || "",
    options: vehicle.options,
    pdmM3Lookup:
      flatForM3.length > 0 && vehicle.sku
        ? buildPdmM3Lookup(flatForM3, vehicle.sku)
        : undefined,
  }

  return { datoVehicle: vehicle, stepsWithPdm, vehicleData, vehicleSource }
}
